import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send, X, Trash2, Minimize2, Maximize2, Expand, Shrink, Sparkles, Search, BarChart3, Clock, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { useManagedFloatingWidgetPlacement } from '@/lib/floatingWidgetLayout';
import { cn } from '@/lib/utils';
import { useAgentChat } from '@/contexts/AgentChatContext';
import { AgentMessage } from './AgentMessage';

interface AgentChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export function AgentChatPanel({ isOpen, onClose, className }: AgentChatPanelProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const minimizedPanelPosition = useManagedFloatingWidgetPlacement(
    'agent-chat-minimized',
    ['agent-chat-minimized'],
  );
  
  const {
    messages,
    isLoading,
    tokensUsed,
    isMinimized,
    isExpanded,
    setMinimized,
    setExpanded,
    sendMessage,
    clearMessages,
    confirmAction,
    cancelAction,
    answerContextQuestion,
    retryAction,
    dismissRetry,
  } = useAgentChat();

  // Auto-scroll to bottom when new messages arrive or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSubmit = () => {
    if (input.trim() && !isLoading) {
      sendMessage(input);
      setInput('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  // Determine panel size classes
  const panelPositionClasses = isMinimized
    ? ""
    : isMobile
    ? "left-2 right-2 bottom-2"
    : "right-4 bottom-4";

  const minimizedPositionStyle = isMinimized
    ? {
        right: `${minimizedPanelPosition.right}px`,
        bottom: `${minimizedPanelPosition.bottom}px`,
      }
    : undefined;

  const sizeClasses = isMinimized
    ? "h-12 w-56"
    : isExpanded
      ? "h-[min(800px,calc(100vh-1rem))] md:w-[720px] md:max-w-[calc(100vw-2rem)]"
      : "h-[min(620px,calc(100vh-1rem))] md:w-[400px] md:max-w-[calc(100vw-2rem)]";

  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col agent-chat-panel transition-all duration-300 ease-out",
        panelPositionClasses,
        sizeClasses,
        className
      )}
      style={minimizedPositionStyle}
    >
      {/* Header - clickable to minimize */}
      <div 
        className={cn(
          "flex items-center agent-chat-header cursor-pointer",
          isMinimized 
            ? "justify-start px-3 py-2 rounded-2xl" 
            : "justify-between px-4 py-3 rounded-t-2xl"
        )}
        onClick={() => setMinimized(!isMinimized)}
      >
        <div className={cn(
          "flex items-center",
          isMinimized ? "gap-2" : "gap-3"
        )}>
          <div className="relative shrink-0">
            <div className={cn(
              "rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/25",
              isMinimized ? "h-8 w-8" : "h-10 w-10"
            )}>
              <img 
                src="/platform-logo-white.svg" 
                alt="SupplySteward" 
                className={isMinimized ? "h-5 w-5" : "h-6 w-6"}
              />
            </div>
            {/* Online indicator */}
            <div className={cn(
              "absolute rounded-full bg-emerald-500 border-2 border-background",
              isMinimized ? "-bottom-0.5 -right-0.5 h-2.5 w-2.5" : "-bottom-0.5 -right-0.5 h-3 w-3"
            )} />
          </div>
          <div className="min-w-0">
            <h3 className={cn(
              "font-semibold truncate",
              isMinimized ? "text-xs" : "text-sm"
            )}>SupplySteward</h3>
            {!isMinimized && (
              <p className="text-xs text-muted-foreground">
                {isLoading ? (
                  <span className="flex items-center gap-1">
                    <span className="agent-typing-indicator">
                      <span className="agent-typing-dot" />
                      <span className="agent-typing-dot" />
                      <span className="agent-typing-dot" />
                    </span>
                    <span className="ml-1">Thinking</span>
                  </span>
                ) : tokensUsed > 0 ? (
                  `${tokensUsed.toLocaleString()} tokens`
                ) : (
                  'Ready to help'
                )}
              </p>
            )}
          </div>
        </div>
        {!isMinimized && (
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                onClick={clearMessages}
                title="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setExpanded(!isExpanded)}
              title={isExpanded ? "Shrink" : "Expand"}
            >
              {isExpanded ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setMinimized(!isMinimized)}
              title="Minimize"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-muted transition-colors"
              onClick={onClose}
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 ? (
              <EmptyState onSuggestionClick={setInput} />
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div 
                    key={message.id} 
                    className="agent-message-enter"
                    style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }}
                  >
                    <AgentMessage 
                      message={message}
                      // Pass previous messages for context extraction in form injection fallback
                      messageHistory={messages.slice(0, index)}
                      onConfirmAction={(actionId) => confirmAction(message.id, actionId)}
                      onCancelAction={(actionId) => cancelAction(message.id, actionId)}
                      onContextQuestionSubmit={(questionId, answers) => answerContextQuestion(message.id, questionId, answers)}
                      onRetryAction={(action) => retryAction(message.id, action)}
                      onDismissRetry={(actionId) => dismissRetry(message.id, actionId)}
                      isLoading={isLoading}
                    />
                  </div>
                ))}
                {/* Scroll anchor - always at the bottom */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-4 agent-input-container rounded-b-2xl">
            <div className="flex gap-3">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Where should we focus today..."
                className="min-h-[48px] max-h-32 resize-none agent-input rounded-xl text-sm"
                disabled={isLoading}
              />
              <Button
                onClick={handleSubmit}
                disabled={!input.trim() || isLoading}
                size="icon"
                className="shrink-0 h-12 w-12 rounded-xl agent-send-button border-0"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ onSuggestionClick }: { onSuggestionClick: (text: string) => void }) {
  const suggestions: { text: string; icon: LucideIcon }[] = [
    { text: "Show me my high-risk suppliers", icon: Search },
    { text: "What's my security insights summary?", icon: BarChart3 },
    { text: "Show me overdue Action items", icon: Clock },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-8 px-2">
      {/* Animated logo */}
      <div className="relative mb-6">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-xl shadow-primary/20 float-animation">
          <img 
            src="/platform-logo-white.svg" 
            alt="SupplySteward" 
            className="h-9 w-9"
          />
        </div>
        <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <Sparkles className="h-3 w-3 text-white" />
        </div>
      </div>
      
      <h4 className="font-semibold text-lg mb-1">SupplySteward</h4>
      <p className="text-sm text-muted-foreground max-w-[280px] mb-6 leading-relaxed">
        Your intelligent assistant for supplier risk management. Ask me anything about your suppliers.
      </p>
      
      <div className="w-full space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Quick Actions
        </p>
        {suggestions.map((suggestion, index) => (
            <SuggestionChip 
            key={index}
            onClick={() => onSuggestionClick(suggestion.text)}
            Icon={suggestion.icon}
          >
            {suggestion.text}
          </SuggestionChip>
        ))}
      </div>
    </div>
  );
}

function SuggestionChip({ 
  children, 
  onClick,
  Icon 
}: { 
  children: string; 
  onClick: () => void;
  Icon: LucideIcon;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-left rounded-xl agent-suggestion-chip group"
    >
      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <span className="flex-1 group-hover:text-primary transition-colors">{children}</span>
      <Send className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}
